/**
 * 對話「session 相關」狀態（UI v2 步 6：由 `ChatPage.tsx` 抽出，零行為改動）
 *
 *   · 之前嘅對話（清單／改名／刪；`decisions/027` 批次 C）
 *   · 出歌輪詢（每 8 秒問狀態；完成／失敗 → `reload()`）
 *   · 分享檔案背景預備（`decisions/018`／2026-09-23 分享修復）
 *   · 加入播放清單嘅目標歌
 *
 * ⚠️ `sheet` 由父層持有（同語言／風格彈窗共用）→ 以 `setSheet` 傳入。
 */
import { useEffect, useState } from 'react';
import type { TFunction } from 'i18next';
import { shareFilename } from '@coeldery/share-button';
import { songAudioUrl } from '../../player/playerModel';
import type { useLibrary } from '../../hooks/useLibrary';
import type { HistoryItem, SheetKind } from './types';

export interface UseSessionDeps {
  t: TFunction;
  deviceId: string;
  sessionId: string;
  startNewChat: () => void;
  songs: ReturnType<typeof useLibrary>['songs'];
  setSheet: (s: SheetKind) => void;
  setArmed: (v: 'exact' | 'meaning' | null) => void;
  exitEdit: () => void;
}

export function useSession(deps: UseSessionDeps) {
  const { t, deviceId, sessionId, startNewChat, songs, setSheet, setArmed, exitEdit } = deps;

  /** ⭐ 0013（`decisions/033`）：要加入播放清單嘅歌（`null` ＝ 彈窗閂住） */
  const [playlistSongId, setPlaylistSongId] = useState<string | null>(null);

  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [historyBusy, setHistoryBusy] = useState(false);
  /** ⭐ 0008 批次 C：改名／刪除對話 */
  const [renameTarget, setRenameTarget] = useState<{ id: string; title: string } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<HistoryItem | null>(null);
  const [historyBusyId, setHistoryBusyId] = useState<string | null>(null);
  const [historyError, setHistoryError] = useState<string | null>(null);

  /** ⭐ 之前嘅對話（一部機） */
  const openHistory = async () => {
    setSheet('history');
    setHistoryBusy(true);
    try {
      const res = await fetch(`/api/chat/sessions?deviceId=${encodeURIComponent(deviceId)}`);
      const data = (await res.json().catch(() => ({}))) as { sessions?: HistoryItem[] };
      setHistory(data.sessions ?? []);
    } catch {
      setHistory([]);
    } finally {
      setHistoryBusy(false);
    }
  };

  /** ⭐ 0008 批次 C：**改名**（`PATCH /api/chat/sessions/:id`；擁有者＝deviceId） */
  const commitRename = async () => {
    if (!renameTarget) return;
    const id = renameTarget.id;
    const title = renameTarget.title.trim();
    if (!title) {
      setHistoryError(t('history_rename_failed'));
      return;
    }
    setHistoryBusyId(id);
    setHistoryError(null);
    try {
      const res = await fetch(`/api/chat/sessions/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ deviceId, title }),
      });
      const data = (await res.json().catch(() => ({}))) as { title?: string; error?: string };
      if (!res.ok) {
        setHistoryError(`${t('history_rename_failed')}${data.error ? `（${data.error}）` : ''}`);
        return;
      }
      const saved = data.title ?? title;
      setHistory((prev) => prev.map((h) => (h.sessionId === id ? { ...h, title: saved } : h)));
      setSheet('history');
      setRenameTarget(null);
    } catch (err) {
      setHistoryError(`${t('history_rename_failed')}（${String(err).slice(0, 60)}）`);
    } finally {
      setHistoryBusyId(null);
    }
  };

  /**
   * ⭐ 0008 批次 C：**刪對話**（硬刪訊息 ＋ 軟刪 session）
   *   ⚠️ **歌會保留**喺「我嘅歌」（library 係 device 級）—— 確認彈窗明文寫咗
   */
  const commitDelete = async () => {
    if (!deleteTarget) return;
    const id = deleteTarget.sessionId;
    setHistoryBusyId(id);
    setHistoryError(null);
    try {
      const res = await fetch(`/api/chat/sessions/${id}?deviceId=${encodeURIComponent(deviceId)}`, {
        method: 'DELETE',
      });
      const data = (await res.json().catch(() => ({}))) as { error?: string };
      if (!res.ok) {
        setHistoryError(`${t('history_delete_failed')}${data.error ? `（${data.error}）` : ''}`);
        return;
      }
      setHistory((prev) => prev.filter((h) => h.sessionId !== id));
      /** 刪咗而家開住嗰個 → 開新對話（唔可以留住一個唔存在嘅 session） */
      if (id === sessionId) {
        setArmed(null);
        exitEdit();
        startNewChat();
      }
      setSheet('history');
      setDeleteTarget(null);
    } catch (err) {
      setHistoryError(`${t('history_delete_failed')}（${String(err).slice(0, 60)}）`);
    } finally {
      setHistoryBusyId(null);
    }
  };

  /**
   * ⭐ **2026-09-23 分享修復**：一出完歌就**背景攞好個 mp3（`File`）**，撳〔分享〕時即刻交畀系統
   *   （唔會 await 任何嘢先 —— 避開手機「用戶手勢」5 秒時限）。
   *   ⚠️ 只攞**最新嘅成功歌**；用戶開咗「省數據」就唔背景攞。
   */
  const [shareFiles, setShareFiles] = useState<Record<string, File>>({});
  useEffect(() => {
    const newest = songs.find((s) => s.status === 'succeeded' && Boolean(s.audioKey));
    if (!newest || !deviceId) return;
    if (shareFiles[newest.id]) return;
    const conn = (navigator as Navigator & { connection?: { saveData?: boolean } }).connection;
    if (conn?.saveData) return;
    let alive = true;
    const url = songAudioUrl(newest.id, { deviceId }, { variant: newest.pickedIndex ?? 0 });
    fetch(url)
      .then((r) => (r.ok ? r.blob() : null))
      .then((b) => {
        if (!alive || !b) return;
        const file = new File([b], shareFilename(newest.title ?? ''), { type: b.type || 'audio/mpeg' });
        setShareFiles((prev) => ({ ...prev, [newest.id]: file }));
      })
      .catch(() => {
        /* 攞唔到都唔致命：撳〔分享〕時會再試 */
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 只跟 songs／deviceId；shareFiles 由 setState 更新
  }, [songs, deviceId]);

  /** 出歌輪詢（`renderingId`）留喺頁面（避免同 `useSongEdit` 互相依賴） */

  return {
    playlistSongId, setPlaylistSongId,
    history, historyBusy, historyBusyId, historyError, setHistoryError,
    renameTarget, setRenameTarget, deleteTarget, setDeleteTarget,
    openHistory, commitRename, commitDelete,
    shareFiles,
  };
}

